import { Component } from "@theme/component";
import { isClickedOutside, normalizeString, onAnimationEnd } from "@theme/utilities";

const HIDDEN = "hidden";
const ARIA_HIDDEN = "aria-hidden";
const DATA_URL = "[data-url]";
const DROPDOWN_LOCALIZATION = "dropdown-localization-component";
const ARIA_EXPANDED = "aria-expanded";
const ARIA_ACTIVEDESCENDANT = "aria-activedescendant";
const KEYDOWN = "keydown";
const SCROLL = "scroll";
const ARIA_SELECTED = "aria-selected";
const TRUE = "true";
const COUNTRY_SELECTOR = ".country";

const FIND_MATCHES_DEFAULT_OPTIONS = {
  // Which data types (label, alias, iso) to match against
  matchLabel: true,
  matchAlias: true,
  matchIso: true,
  matchCurrency: true,
  // If true, the search value must match the start of the label
  labelMatchStart: false,
  // If true, a result will not display unless the search value equals an alias in its entirety
  aliasExactMatch: false,
};

/**
 * A custom element that displays a localization form.
 *
 * @typedef {object} FormRefs
 * @property {HTMLDivElement} countryList - The country list element.
 * @property {HTMLInputElement} countryInput - The country input element.
 * @property {HTMLLIElement[]} countryListItems - The country list items element.
 * @property {HTMLFormElement} form - The form element.
 * @property {HTMLDivElement} liveRegion - The live region element.
 * @property {HTMLInputElement} languageInput - The language input element.
 * @property {HTMLButtonElement} languageToggle - The language toggle button.
 * @property {HTMLUListElement} languageList - The language options list.
 * @property {HTMLLIElement[]} languageOptions - The language option items.
 * @property {HTMLSpanElement} languageValue - The displayed language value.
 * @property {HTMLSpanElement} noResultsMessage - The no results message element.
 * @property {HTMLUListElement} popularCountries - The popular countries element.
 * @property {HTMLInputElement} search - The search input element.
 * @property {HTMLButtonElement} resetButton - The reset button element.
 *
 * @extends {Component<FormRefs>}
 */
class LocalizationFormComponent extends Component {
  /**
   * Handles the keydown event for the container.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #onContainerKeyDown = event => {
    const { countryInput, countryListItems } = this.refs;

    switch (event.key) {
      case "ArrowUp": {
        event.preventDefault();
        event.stopPropagation();
        this.#changeCountryFocus("UP");
        break;
      }
      case "ArrowDown": {
        event.preventDefault();
        event.stopPropagation();
        this.#changeCountryFocus("DOWN");
        break;
      }
      case "Enter": {
        event.preventDefault();
        event.stopPropagation();
        const focusedItem = countryListItems.find(item => item.getAttribute(ARIA_SELECTED) === TRUE);

        if (focusedItem) {
          const { languageInput } = this.refs;
          countryInput.value = focusedItem.dataset.value ?? "";
          if (languageInput) {
            languageInput.disabled = true;
          }
          this.#submitForm(focusedItem.dataset.url);
        }
        break;
      }
      default: {
        break;
      }
    }

    if (!this.refs.search) {
      return;
    }

    setTimeout(() => {
      const focusableItems = this.refs.countryListItems.filter(item => !item.closest(`[${HIDDEN}]`));
      const focusedItemIndex = this.#focusedIndex(focusableItems);
      const focusedItem = focusableItems[focusedItemIndex];

      if (focusedItem) {
        this.refs.search.setAttribute(ARIA_ACTIVEDESCENDANT, focusedItem.id);
      } else {
        this.refs.search.setAttribute(ARIA_ACTIVEDESCENDANT, "");
      }
    });
  };

  /**
   * Selects a country.
   *
   * @param {string} countryName - The name of the country to select.
   * @param {Event} event - The event object.
   */
  selectCountry = (countryName, event) => {
    event.preventDefault();
    const { countryInput, languageInput } = this.refs;

    countryInput.value = countryName;
    if (languageInput) {
      languageInput.disabled = true;
    }
    this.#submitForm(this.#targetUrlFrom(event));
  };

  /**
   * Handles the keydown event for the search input.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #onSearchKeyDown = event => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.#onContainerKeyDown(event);
  };

  /**
   * Focuses the search input or the current country when filtering is unavailable.
   */
  focusSearchInput = () => {
    const { search, countryInput, countryListItems = [] } = this.refs;

    if (search) {
      search.focus();
      return;
    }

    const visibleItems = countryListItems.filter(item => !item.closest(`[${HIDDEN}]`));
    const selected = visibleItems.find(item => item.dataset.value === countryInput?.value) ?? visibleItems[0];
    for (const item of countryListItems) {
      item.setAttribute(ARIA_SELECTED, item === selected ? TRUE : "false");
    }
    selected?.focus();
  };

  /**
   * Handles the scroll event on the country list.
   *
   * @param {Event} event - The scroll event object.
   */
  #onCountryListScroll = event => {
    const countryFilter = this.querySelector(".country-filter");
    const countryList = event.target instanceof HTMLElement ? event.target : null;

    if (countryFilter && countryList) {
      const shouldShowBorder = countryList.scrollTop > 0;
      countryFilter.classList.toggle("is-scrolled", shouldShowBorder);
    }
  };

  /**
   * Closes the language list when focus leaves it (e.g. Tab key).
   *
   * @param {FocusEvent} event - The focusout event object.
   */
  #onLanguageFocusOut = event => {
    if (this.refs.languageList?.contains(/** @type {Node} */ (event.relatedTarget))) {
      return;
    }
    if (this.refs.languageToggle?.contains(/** @type {Node} */ (event.relatedTarget))) {
      return;
    }
    this.refs.languageList?.setAttribute(ARIA_HIDDEN, TRUE);
    this.refs.languageToggle?.setAttribute(ARIA_EXPANDED, "false");
  };

  /**
   * Handles keydown on the language list for arrow navigation and Enter selection.
   * Uses keydown so preventDefault suppresses the browser's default scroll behavior.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #onLanguageKeyDown = event => {
    const { languageOptions } = this.refs;

    switch (event.key) {
      case "ArrowDown": {
        event.preventDefault();
        event.stopPropagation();
        const focusableOptions = languageOptions.filter(opt => !opt.hasAttribute(HIDDEN));
        const currentIndex = this.#focusedIndex(focusableOptions);
        const next =
          currentIndex < focusableOptions.length - 1 ? focusableOptions[currentIndex + 1] : focusableOptions[0];
        next?.focus();
        break;
      }
      case "ArrowUp": {
        event.preventDefault();
        event.stopPropagation();
        const focusableOptions = languageOptions.filter(opt => !opt.hasAttribute(HIDDEN));
        const currentIndex = this.#focusedIndex(focusableOptions);
        const prev = currentIndex > 0 ? focusableOptions[currentIndex - 1] : focusableOptions.at(-1);
        prev?.focus();
        break;
      }
      case "Enter": {
        event.preventDefault();
        event.stopPropagation();
        const activeItem = /** @type {HTMLElement | null} */ (document.activeElement);
        if (activeItem?.dataset?.value) {
          this.selectLanguage(activeItem.dataset.value, event);
        }
        break;
      }
      default: {
        break;
      }
    }
  };

  /**
   * Handles keyup on the language list for Escape key.
   * Uses keyup (not keydown) to match DropdownLocalizationComponent's Escape handler,
   * so stopPropagation prevents the parent from also closing the entire panel.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #onLanguageKeyUp = event => {
    if (event.key !== "Escape") {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const { languageList, languageToggle } = this.refs;

    if (languageToggle) {
      languageToggle.focus();
      languageToggle.setAttribute(ARIA_EXPANDED, "false");
    }
    if (languageList) {
      languageList.setAttribute(ARIA_HIDDEN, TRUE);
    }
  };

  /** Route localization intent through the shared bounded desktop predictor. @param {Event} event */
  prefetchUrl(event) {
    const baseTargetUrl = this.#targetUrlFrom(event);
    if (!baseTargetUrl) {
      return;
    }

    const urlObj = new URL(baseTargetUrl, globalThis.location.origin);
    urlObj.search = globalThis.location.search;
    const url = urlObj.toString();

    globalThis.__themeNavigationPredictor?.warm(url);
  }

  /**
   * Resolves the data-url target carried by the option element under the event.
   *
   * @param {Event} event - The event object.
   * @returns {string | undefined} The target URL, if any.
   */
  #targetUrlFrom(event) {
    return event.target instanceof Element
      ? /** @type {HTMLElement | null} */ (event.target.closest(DATA_URL))?.dataset?.url
      : undefined;
  }

  /**
   * Index of the currently-focused element within a list of focusable items.
   *
   * @param {HTMLLIElement[]} items - The candidate list items.
   * @returns {number} The index of document.activeElement, or -1.
   */
  #focusedIndex(items) {
    return items.indexOf(/** @type {HTMLLIElement} */ (document.activeElement));
  }

  /**
   * Submits the localization form via fetch() to set the session cookie in the
   * background, then reloads the page. This keeps the page visible during the
   * POST (no blank screen), preserves scroll position, and lets view transitions
   * handle the visual update for both language and country/currency changes.
   *
   * @param {string} [baseTargetUrl] - The base target URL to navigate to after the form is submitted.
   * @param {boolean} [isLanguageChange] - Whether this is a language change.
   */
  async #submitForm(baseTargetUrl = globalThis.location.href, isLanguageChange = false) {
    const { form } = this.refs;
    if (!form) {
      return;
    }

    // Ensure we keep query parameters when navigating to a new localized URL
    const url = new URL(baseTargetUrl, globalThis.location.origin);
    url.search = globalThis.location.search;
    const targetUrl = url.toString();

    // Close any open localization UI immediately for instant feedback
    const dropdown = /** @type {DropdownLocalizationComponent | null} */ (this.closest(DROPDOWN_LOCALIZATION));
    if (dropdown) {
      dropdown.hidePanel();
    }

    const details = this.closest("details");
    if (details) {
      details.removeAttribute("open");
    }

    const headerDrawer = /** @type {any} */ (this.closest("header-drawer"));
    if (headerDrawer) {
      headerDrawer.close();
    }

    const returnToInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="return_to"]'));
    if (returnToInput) {
      returnToInput.value = url.pathname + url.search;
    }

    try {
      await fetch(form.action, {
        method: "POST",
        body: new FormData(form),
        redirect: "manual",
      });

      // For language changes, update the URL to the localized path before
      // reloading so the browser loads the correct language version.
      // replaceState + reload preserves scroll position (unlike location.assign
      // which navigates to a new URL and resets scroll to the top).
      if (isLanguageChange && targetUrl !== globalThis.location.href) {
        history.replaceState(null, "", targetUrl);
      }

      globalThis.location.reload();
    } catch {
      form.submit();
    }
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.refs.search) {
      this.refs.search.addEventListener(KEYDOWN, this.#onSearchKeyDown);
    }

    if (this.refs.countryList) {
      this.refs.countryList.addEventListener(KEYDOWN, this.#onContainerKeyDown);
      this.refs.countryList.addEventListener(SCROLL, this.#onCountryListScroll);
    }

    if (this.refs.languageList) {
      this.refs.languageList.addEventListener(KEYDOWN, this.#onLanguageKeyDown);
      this.refs.languageList.addEventListener("keyup", this.#onLanguageKeyUp);
      this.refs.languageList.addEventListener("focusout", this.#onLanguageFocusOut);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.refs.search) {
      this.refs.search.removeEventListener(KEYDOWN, this.#onSearchKeyDown);
    }

    if (this.refs.countryList) {
      this.refs.countryList.removeEventListener(KEYDOWN, this.#onContainerKeyDown);
      this.refs.countryList.removeEventListener(SCROLL, this.#onCountryListScroll);
    }

    if (this.refs.languageList) {
      this.refs.languageList.removeEventListener(KEYDOWN, this.#onLanguageKeyDown);
      this.refs.languageList.removeEventListener("keyup", this.#onLanguageKeyUp);
      this.refs.languageList.removeEventListener("focusout", this.#onLanguageFocusOut);
    }
  }

  /**
   * Selects a language option and submits the form.
   *
   * @param {string} languageCode - The language ISO code to select.
   * @param {Event} event - The event object.
   */
  selectLanguage(languageCode, event) {
    event.preventDefault();
    const { languageInput, countryInput, languageList, languageOptions, languageToggle, languageValue } = this.refs;

    if (!languageInput) {
      return;
    }

    languageInput.value = languageCode;
    if (countryInput) {
      countryInput.disabled = true;
    }

    // Update aria-selected on options
    for (const option of languageOptions) {
      const isSelected = option.dataset.value === languageCode;
      option.setAttribute(ARIA_SELECTED, isSelected ? TRUE : "false");

      if (isSelected && languageValue) {
        languageValue.textContent = option.textContent.trim();
      }
    }

    // Close the list — move focus out before hiding to avoid aria-hidden warning
    if (languageList?.contains(document.activeElement)) {
      /** @type {HTMLElement} */ (document.activeElement).blur();
    }
    if (languageList) {
      languageList.setAttribute(ARIA_HIDDEN, TRUE);
    }
    if (languageToggle) {
      languageToggle.setAttribute(ARIA_EXPANDED, "false");
    }

    this.#submitForm(this.#targetUrlFrom(event), true);
  }

  /**
   * Toggles the language options list open/closed.
   */
  toggleLanguageList() {
    const { languageList, languageOptions, languageToggle } = this.refs;

    if (!languageList || !languageToggle) {
      return;
    }

    const isOpen = languageList.getAttribute(ARIA_HIDDEN) !== TRUE;

    if (isOpen) {
      languageToggle.focus();
      languageList.setAttribute(ARIA_HIDDEN, TRUE);
      languageToggle.setAttribute(ARIA_EXPANDED, "false");
    } else {
      languageList.removeAttribute(ARIA_HIDDEN);
      languageToggle.setAttribute(ARIA_EXPANDED, TRUE);
      const selected = languageOptions.find(opt => opt.getAttribute(ARIA_SELECTED) === TRUE);
      (selected ?? languageOptions[0])?.focus();
    }
  }

  /**
   * Finds matches for a given search value in a country element.
   *
   * @typedef {Object} Options
   * @property {boolean} [matchLabel] - Whether to match the label.
   * @property {boolean} [matchAlias] - Whether to match the alias.
   * @property {boolean} [matchIso] - Whether to match the iso.
   * @property {boolean} [matchCurrency] - Whether to match the currency.
   * @property {boolean} [labelMatchStart] - Whether to match the label start.
   * @property {boolean} [aliasExactMatch] - Whether to match the alias exact match.
   *
   * @typedef {Object} MatchTypes
   * @property {boolean} [label] - Whether the label matches the search value.
   * @property {boolean} [alias] - Whether the alias matches the search value.
   * @property {boolean} [iso] - Whether the iso matches the search value.
   * @property {boolean} [currency] - Whether the currency matches the search value.
   *
   * @param {string} searchValue - The search value to find matches for.
   * @param {HTMLElement} countryEl - The country element to find matches in.
   * @param {Options} options - The options for the search.
   * @returns {MatchTypes} The matches found in the country element.
   */
  #findMatches(searchValue, countryEl, options = FIND_MATCHES_DEFAULT_OPTIONS) {
    const matchTypes = {};
    const { aliases, value: iso } = countryEl.dataset;

    if (options.matchLabel) {
      const countryName = normalizeString(countryEl.querySelector(COUNTRY_SELECTOR)?.textContent ?? "");

      if (!countryName) {
        return matchTypes;
      }

      matchTypes.label = options.labelMatchStart
        ? countryName.startsWith(searchValue)
        : countryName.includes(searchValue);
    }

    if (options.matchCurrency) {
      const currency = normalizeString(countryEl.querySelector(".localization-form__currency")?.textContent ?? "");
      matchTypes.currency = currency.includes(searchValue);
    }

    if (options.matchIso) {
      matchTypes.iso = normalizeString(iso ?? "") === searchValue;
    }

    if (options.matchAlias) {
      const countryAliases = aliases?.split(",").map(alias => normalizeString(alias));

      if (!countryAliases) {
        return matchTypes;
      }

      matchTypes.alias = countryAliases.some(alias =>
        options.aliasExactMatch ? alias === searchValue : alias.startsWith(searchValue),
      );
    }

    return matchTypes;
  }

  /**
   * Highlights matching text in a string by wrapping it in <mark> tags.
   *
   * @param {string | null} text - The text to highlight.
   * @param {string} searchValue - The search value to highlight.
   * @returns {string} The text with matching parts wrapped in <mark> tags.
   */
  #highlightMatches(text, searchValue) {
    if (!text || !searchValue) {
      return text ?? "";
    }

    const normalizedText = normalizeString(text);
    const normalizedSearch = normalizeString(searchValue);
    const startIndex = normalizedText.indexOf(normalizedSearch);

    if (startIndex === -1) {
      return text;
    }

    const endIndex = startIndex + normalizedSearch.length;
    const before = text.slice(0, startIndex);
    const match = text.slice(startIndex, endIndex);
    const after = text.slice(endIndex);

    return `${before}<mark>${match}</mark>${after}`;
  }

  /**
   * Filters the countries based on the search value.
   */
  filterCountries() {
    const { countryList, countryListItems, liveRegion, noResultsMessage, popularCountries, resetButton, search } =
      this.refs;
    const { labelResultsCount } = this.dataset;
    const searchValue = normalizeString(search.value);
    let countVisibleCountries = 0;

    resetButton.toggleAttribute(HIDDEN, !searchValue);

    if (popularCountries) {
      popularCountries.toggleAttribute(HIDDEN, Boolean(searchValue));
    }

    const wrapper = this.querySelector(".country-selector-form__wrapper");
    if (wrapper) {
      wrapper.classList.toggle("is-searching", Boolean(searchValue));
    }

    for (const countryEl of countryListItems) {
      if (searchValue === "") {
        countryEl.removeAttribute(HIDDEN);
        const countrySpan = countryEl.querySelector(COUNTRY_SELECTOR);
        if (countrySpan) {
          const originalText = countrySpan.textContent;
          countrySpan.textContent = originalText;
        }
        countVisibleCountries++;
      } else {
        const matches = this.#findMatches(searchValue, countryEl);

        if (matches.label || matches.alias || matches.iso || matches.currency) {
          countryEl.removeAttribute(HIDDEN);
          const countrySpan = countryEl.querySelector(COUNTRY_SELECTOR);
          if (countrySpan) {
            countrySpan.innerHTML = this.#highlightMatches(countrySpan.textContent, searchValue);
          }
          countVisibleCountries++;
        } else {
          countryEl.setAttribute(HIDDEN, "");
        }
      }
    }

    if (liveRegion && labelResultsCount) {
      liveRegion.textContent = labelResultsCount.replace("[count]", `${countVisibleCountries}`);
    }

    noResultsMessage.hidden = countVisibleCountries > 0;
    countryList.scrollTop = 0;
  }

  /**
   * Changes the focus of the country list items.
   *
   * @param {string} direction - The direction to change the focus.
   */
  #changeCountryFocus(direction) {
    const { countryListItems } = this.refs;
    const focusableItems = countryListItems.filter(item => !item.closest(`[${HIDDEN}]`));
    const focusedItemIndex = this.#focusedIndex(focusableItems);
    let itemToFocus;

    if (direction === "UP") {
      itemToFocus = focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems.at(-1);
    } else {
      itemToFocus =
        focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
    }

    for (const item of countryListItems) {
      item.setAttribute(ARIA_SELECTED, item === itemToFocus ? TRUE : "false");
    }
    itemToFocus?.focus();
  }

  /**
   * Resets the countries filter.
   *
   * @param {Event} event - The event object.
   */
  resetCountriesFilter(event) {
    const { search } = this.refs;

    event.stopPropagation();
    search.value = "";
    this.filterCountries();
    search.setAttribute(ARIA_ACTIVEDESCENDANT, "");
    search.focus();
  }

  /**
   * Resets the form.
   */
  resetForm() {
    const { search } = this.refs;

    if (!search) {
      return;
    }

    if (search.value !== "") {
      search.value = "";
      this.filterCountries();
      search.setAttribute(ARIA_ACTIVEDESCENDANT, "");
    }
  }

  /**
   * Resets the language list to its collapsed state.
   * Called when the parent panel closes so state is clean on next open.
   */
  resetLanguageList() {
    const { languageList, languageToggle } = this.refs;
    if (languageList?.contains(document.activeElement)) {
      /** @type {HTMLElement} */ (document.activeElement).blur();
    }
    if (languageList) {
      languageList.setAttribute(ARIA_HIDDEN, TRUE);
    }
    if (languageToggle) {
      languageToggle.setAttribute(ARIA_EXPANDED, "false");
    }
  }
}

/**
 * A custom element that displays a dropdown localization form.
 *
 * @typedef {object} DropdownRefs
 * @property {HTMLButtonElement} button - The button element.
 * @property {HTMLDivElement} panel - The panel element.
 * @property {LocalizationFormComponent} localizationForm - The localization form component.
 *
 * @extends {Component<DropdownRefs>}
 */
class DropdownLocalizationComponent extends Component {
  /**
   * Hides the panel.
   */
  hidePanel = () => {
    if (this.isHidden) {
      return;
    }

    this.removeEventListener("keyup", this.#handleKeyUp);
    document.removeEventListener("click", this.#handleClickOutside);

    this.refs.button?.setAttribute(ARIA_EXPANDED, "false");
    this.refs.panel.setAttribute(HIDDEN, "");
    this.refs.localizationForm?.resetForm();
    this.refs.localizationForm?.resetLanguageList();
  };

  /**
   * Handles the click outside event.
   *
   * @param {PointerEvent} event - The event object.
   */
  #handleClickOutside = event => {
    if (isClickedOutside(event, this)) {
      this.hidePanel();
    }
  };

  /**
   * Handles the keyup event.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #handleKeyUp = event => {
    if (event.key !== "Escape") {
      return;
    }

    this.hidePanel();
    event.stopPropagation();
    this.refs.button?.focus();
  };

  get isHidden() {
    return this.refs.panel.hasAttribute(HIDDEN);
  }

  /**
   * Toggles the panel.
   */
  toggleSelector() {
    return this.isHidden ? this.showPanel() : this.hidePanel();
  }

  /**
   * Shows the panel.
   */
  showPanel() {
    if (!this.isHidden) {
      return;
    }

    this.addEventListener("keyup", this.#handleKeyUp);
    document.addEventListener("click", this.#handleClickOutside);

    this.refs.panel.removeAttribute(HIDDEN);
    this.refs.button.setAttribute(ARIA_EXPANDED, TRUE);

    onAnimationEnd(this.refs.panel, () => {
      this.#updateWidth();
      this.refs.localizationForm?.focusSearchInput();
    });
  }

  /**
   * Updates the width of the panel.
   */
  #updateWidth() {
    this.style.setProperty("--width", `${this.refs.localizationForm.offsetWidth}px`);
  }
}

/**
 * A custom element that displays a drawer localization form.
 *
 * @typedef {object} DrawerRefs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {LocalizationFormComponent} localizationForm - The localization form component.
 *
 * @extends {Component<DrawerRefs>}
 */
class DrawerLocalizationComponent extends Component {
  /**
   * Handles the scroll event on the country list.
   *
   * @param {Event} event - The scroll event object.
   */
  #onCountryListScroll = event => {
    const countryFilter = this.querySelector(".country-filter");
    const countryList = event.target instanceof HTMLElement ? event.target : null;

    if (countryFilter && countryList) {
      const shouldShowBorder = countryList.scrollTop > 0;
      countryFilter.classList.toggle("is-scrolled", shouldShowBorder);
    }
  };

  /**
   * Toggles the dialog.
   *
   * @param {ToggleEvent} event - The event object.
   */
  toggle(event) {
    const { target } = event;
    const { localizationForm } = this.refs;

    if (!localizationForm || !(target instanceof HTMLDetailsElement)) {
      return;
    }

    const countryList = localizationForm.querySelector(".country-selector-form__wrapper");

    if (target.open) {
      if (countryList) {
        countryList.addEventListener(SCROLL, this.#onCountryListScroll);
      }
      // A header submenu defers focus until its opening transition and trap settle.
      if (!this.closest("header-drawer")) {
        localizationForm.focusSearchInput();
      }
    } else {
      countryList?.removeEventListener(SCROLL, this.#onCountryListScroll);
      localizationForm.resetForm();
      localizationForm.resetLanguageList();
    }
  }
}

if (!customElements.get("localization-form-component")) {
  customElements.define("localization-form-component", LocalizationFormComponent);
}

if (!customElements.get(DROPDOWN_LOCALIZATION)) {
  customElements.define(DROPDOWN_LOCALIZATION, DropdownLocalizationComponent);
}

if (!customElements.get("drawer-localization-component")) {
  customElements.define("drawer-localization-component", DrawerLocalizationComponent);
}
